import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Link,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { environmentsAPI } from '../services/api';

function EnvironmentSummary() {
  const [environments, setEnvironments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regionFilter, setRegionFilter] = useState('all');
  const [environmentFilter, setEnvironmentFilter] = useState('all');
  const [systemFilter, setSystemFilter] = useState('all');

  useEffect(() => {
    loadEnvironments();
  }, []);

  const loadEnvironments = async () => {
    try {
      setLoading(true);
      const response = await environmentsAPI.list();
      setEnvironments(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load environments');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatUrl = (url) => {
    if (!url) return '';
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  const getEnvColor = (envType) => {
    if (envType === 'Development') return 'info';
    if (envType === 'Test') return 'warning';
    if (envType === 'Production') return 'success';
    return 'default';
  };

  const getSystemColor = (systemType) => {
    return systemType === 'LIMS' ? 'primary' : 'secondary';
  };

  // Get unique regions, environment types, and system types
  const regions = [...new Set(environments.map(env => env.location).filter(Boolean))].sort();
  const environmentTypes = [...new Set(environments.map(env => env.environment_type))].sort();
  const systemTypes = [...new Set(environments.map(env => env.system_type))].sort();

  // Filter environments by region, environment type, and system type
  const filteredEnvironments = environments
    .filter(env => regionFilter === 'all' || env.location === regionFilter)
    .filter(env => environmentFilter === 'all' || env.environment_type === environmentFilter)
    .filter(env => systemFilter === 'all' || env.system_type === systemFilter);

  // Sort alphabetically by customer name
  const sortedEnvironments = [...filteredEnvironments].sort((a, b) =>
    a.cust_name.localeCompare(b.cust_name)
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4">Environment Summary</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Filter by Region</FormLabel>
          <RadioGroup
            row
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
          >
            <FormControlLabel value="all" control={<Radio />} label="Global (All Regions)" />
            {regions.map((region) => (
              <FormControlLabel
                key={region}
                value={region}
                control={<Radio />}
                label={region}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <FormControl component="fieldset">
          <FormLabel component="legend">Filter by System Type</FormLabel>
          <RadioGroup
            row
            value={systemFilter}
            onChange={(e) => setSystemFilter(e.target.value)}
          >
            <FormControlLabel value="all" control={<Radio />} label="All Systems" />
            {systemTypes.map((sysType) => (
              <FormControlLabel
                key={sysType}
                value={sysType}
                control={<Radio />}
                label={sysType}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <FormControl component="fieldset">
          <FormLabel component="legend">Filter by Environment Type</FormLabel>
          <RadioGroup
            row
            value={environmentFilter}
            onChange={(e) => setEnvironmentFilter(e.target.value)}
          >
            <FormControlLabel value="all" control={<Radio />} label="All Types" />
            {environmentTypes.map((envType) => (
              <FormControlLabel
                key={envType}
                value={envType}
                control={<Radio />}
                label={envType}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>

      <Paper sx={{ mb: 2, p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {sortedEnvironments.length} environment{sortedEnvironments.length !== 1 ? 's' : ''}
          {systemFilter !== 'all' && ` - ${systemFilter}`}
          {environmentFilter !== 'all' && ` (${environmentFilter})`}
          {regionFilter !== 'all' && ` in ${regionFilter}`}
        </Typography>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>System</TableCell>
              <TableCell>Environment</TableCell>
              <TableCell>URL</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedEnvironments.map((env) => (
              <TableRow key={env.id}>
                <TableCell>
                  <strong>{env.cust_name}</strong>
                </TableCell>
                <TableCell>{env.cust_code}</TableCell>
                <TableCell>{env.location || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={env.system_type}
                    size="small"
                    color={getSystemColor(env.system_type)}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={env.environment_type}
                    size="small"
                    color={getEnvColor(env.environment_type)}
                  />
                </TableCell>
                <TableCell>
                  <Link
                    href={formatUrl(env.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    {env.url}
                    <OpenInNewIcon fontSize="small" />
                  </Link>
                </TableCell>
              </TableRow>
            ))}
            {sortedEnvironments.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No environments found
                  {systemFilter !== 'all' && ` - ${systemFilter}`}
                  {environmentFilter !== 'all' && ` (${environmentFilter})`}
                  {regionFilter !== 'all' && ` in ${regionFilter}`}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default EnvironmentSummary;
